import FilterDate from '@/Enums/FilterDate.enum';
import type Dimension from '@/Enums/ReportBuilder/Dimension.enum';
import type TimeBucket from '@/Enums/ReportBuilder/TimeBucket.enum';
import type { IControlOption } from '@/Interfaces/ControlOption.interface';
import type { IFilterFormData } from '@/Interfaces/ReportBuilder/FilterFormData.interface';
import type { IScheduleFormData } from '@/Interfaces/ReportBuilder/ScheduleFormData.interface';
import type { IScheduledCustomReport } from '@/Interfaces/ReportBuilder/ScheduledCustomReport.interface';
import type {
  ICustomReportTemplate,
  IHydratedCustomReportTemplate,
} from '@/Interfaces/ReportBuilder/Template.interface';
import { MOCK_ROWS, createMetricOption, hydrateOptionGroup } from '@/constants/reportBuilder';
import { createPlatformOption } from '@/constants/reportBuilder/omniPlatform';
import { DateRange } from '@mui/x-date-pickers-pro';
import { isAxiosError } from 'axios';
import moment, { Moment } from 'moment';
import { formatClientsResponseData } from './clients';
import { getShortcutItemsInclusive } from './datePicker/shortcuts';

export const formatToColumns = (metric: { value: string; label: string }) => {
  const { value: field, label: headerName } = metric;
  return {
    field,
    headerName,
  };
};

export const getDefaultFilters = (): IFilterFormData => {
  const rangeStart = moment().subtract(6, 'days');
  const rangeEnd = moment();

  return {
    dateRange: [rangeStart, rangeEnd],
    dateRangeFormat: FilterDate.LAST_7_DAYS,
    timeBucket: '',
    // @ts-expect-error default template value
    template: '',
    dimensions: [],
    metrics: [],
    clients: [],
    advertisers: [],
    campaigns: [],
    events: [],
    platforms: [],
  };
};

export const getMockRows = (timeBucket?: TimeBucket | '') => {
  return MOCK_ROWS.map((row, id) => ({ ...row, id, timeBucket }));
};

/**
 * `visibleDimensions` drops dimensions the current user cannot see, so a template saved with a
 * gated dimension (Organization, rate card, alternative IDs) does not surface it to someone the
 * backend would strip it for anyway.
 */
export const hydrateTemplate = (
  template: ICustomReportTemplate,
  visibleDimensions?: Dimension[],
): IHydratedCustomReportTemplate => {
  const shortcuts = getShortcutItemsInclusive();
  const { dateRangeFormat, dateRange, metrics, dimensions, clients, platforms } = template;
  const parsedDateRange: DateRange<Moment> =
    dateRangeFormat === FilterDate.CUSTOM_RANGE
      ? (dateRange.map(date => (date ? moment(date) : null)) as DateRange<Moment>)
      : (shortcuts.find(shortcut => shortcut.value === dateRangeFormat)?.getValue() as DateRange<Moment>);

  return {
    ...template,
    dateRange: parsedDateRange,
    metrics: (metrics ?? []).map(metric => createMetricOption(metric)),
    dimensions: hydrateOptionGroup(
      (dimensions ?? []).filter(dimension => !visibleDimensions || visibleDimensions.includes(dimension)),
      '',
    ),
    clients: formatClientsResponseData(clients),
    platforms: platforms?.length ? platforms.map(createPlatformOption) : [],
  };
};

export const toScheduleFormDefaults = (report: IScheduledCustomReport): IScheduleFormData => ({
  reportName: report.name,
  interval: report.interval,
  recipientEmail: '',
  recipientEmails: report.recipientEmails ?? [],
  startDate: moment(report.deliveryStartAt),
  dateRangeFormat: report.dateRangeFormat,
});

export const getReportGenerationErrorMessage = (error: unknown): string =>
  (isAxiosError(error) && error.response?.status === 400
    ? (error.response.data?.message as string | undefined)?.replace(/^E_[A-Z_]+: /, '')
    : undefined) || 'Something went wrong during custom report generation.';

export function getOverlappingColumns<T extends IControlOption<unknown, unknown>>(...args: T[][]): T[] {
  if (!args || args?.length <= 1) {
    return args?.[0] ?? [];
  }
  const orderedArrays = [...args.sort((a, b) => a.length - b.length).filter(arr => !!arr.length)];

  let [baseArray] = orderedArrays.splice(0, 1);

  for (const currentArray of orderedArrays) {
    baseArray = baseArray.filter(record => currentArray.some(lookupRecord => lookupRecord.value === record.value));
  }

  return baseArray;
}
